import { Spacing } from '@persona-id/design-tokens';
import { capitalize, inRange } from 'lodash/fp';
import { Fragment, memo } from 'react';
import { Field, FormSection } from 'redux-form';
import styled from 'styled-components';

import {
  DEFAULT_MAX_CHAR_LENGTH,
  DELETE_DISABLED_TEXT,
  LABEL_VALIDATION_MESSAGE,
  MAX_LENGTH_VALIDATION_MESSAGE,
  PANEL_FOOTER_HEIGHT,
  PANEL_HEADER_HEIGHT,
} from 'dashboard/apps/InquiryTemplateEditor/FieldPanel/shared';
import { CONFIG_EDITOR_RIGHT_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import FormComponentOptionsEditor from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/FormComponentOptionsEditor';
import InputField from 'dashboard/components/FormControls/InputField';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import SelectField, { Option } from 'dashboard/components/FormControls/SelectField';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { COUNTRIES } from 'lib/constants/locales';
import {
  FIELD_POLICY_OPTIONS,
  FIELD_SCHEMA_TYPE_DISPLAY,
  FieldSchema,
  FieldSchemaType,
  isFieldSchemaWithChoices,
  STRING_SANITIZATION_OPTIONS,
} from 'lib/fields';
import { isRequired, withMessage } from 'lib/utils/formValidation';

const FIELD_INPUT_PLACEHOLDER = 'Enter value';
const DISABLED_EMPTY_PLACEHOLDER = '--';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  height: ${PANEL_HEADER_HEIGHT}px;
  padding: ${Spacing[5]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const BodyContainer = styled.div`
  display: grid;
  height: calc(100% - ${PANEL_HEADER_HEIGHT + PANEL_FOOTER_HEIGHT});
  grid-template-columns: 1fr;
  grid-gap: ${Spacing[3]};
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  padding: ${Spacing[5]};
  padding-bottom: ${Spacing[6]};
  overflow-y: auto;
`;

const FooterContainer = styled.footer`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

export const InputLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
`;

interface FieldPanelProps {
  name: string;
  field: FieldSchema;
  hasReferences: boolean;
  referencedByCountrySelect: boolean;
  editable: boolean;
  handleDeleteField: (fieldKey: string) => void;
  handleClose: () => void;
}

const validate = {
  label: [withMessage(LABEL_VALIDATION_MESSAGE, isRequired)],
  'max-char-length': [
    withMessage(MAX_LENGTH_VALIDATION_MESSAGE, (length: number) =>
      inRange(1, DEFAULT_MAX_CHAR_LENGTH + 1, length),
    ),
  ],
};

const COUNTRY_CODE_FIELD_TYPE = 'country-code';

const FieldPanel = ({
  name = '',
  field,
  hasReferences,
  referencedByCountrySelect,
  editable,
  handleDeleteField,
  handleClose,
}: FieldPanelProps) => {
  const { key, type } = field;
  const deleteField = () => handleDeleteField(key);

  return (
    <FormSection name={name} component={Fragment}>
      <Container>
        <HeaderContainer>
          <Heading size='sm'>Edit field</Heading>
        </HeaderContainer>
        <BodyContainer>
          <FieldWrapper>
            <Field
              size='md'
              type='text'
              id='label'
              name='label'
              label={<InputLabel>Name</InputLabel>}
              placeholder={FIELD_INPUT_PLACEHOLDER}
              component={InputField}
              disabled={!editable}
              validate={validate.label}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Field
              size='md'
              type='text'
              id='key'
              name='key'
              placeholder={DISABLED_EMPTY_PLACEHOLDER}
              label={<InputLabel>Key</InputLabel>}
              component={InputField}
              disabled
            />
            <Spacer height={1} />
            <Paragraph>Keys for existing fields cannot be updated.</Paragraph>
          </FieldWrapper>
          <FieldWrapper>
            <Field
              size='md'
              id='type'
              name='type'
              placeholder={DISABLED_EMPTY_PLACEHOLDER}
              label={<InputLabel>Type</InputLabel>}
              component={SelectField}
              disabled
            >
              {Object.keys(FIELD_SCHEMA_TYPE_DISPLAY).map((fieldType) => (
                <Option key={fieldType} value={fieldType}>
                  {`${FIELD_SCHEMA_TYPE_DISPLAY[type] ?? type}`}
                </Option>
              ))}
            </Field>
          </FieldWrapper>
          <DefaultValueInput
            // Temporary type override to provide country code dropdown for default value
            // until we support country code as a field type
            fieldType={referencedByCountrySelect ? COUNTRY_CODE_FIELD_TYPE : type}
            disabled={!editable}
          />
          {isFieldSchemaWithChoices(field) && (
            <div>
              <FormComponentOptionsEditor
                options={(field.config.options ?? []).map((value) => ({
                  // Note (Evan): Using the option value as `key` to hold reference
                  // to the original value of the option to use when updating component options
                  // translations are purposefully omitted when editing in the non-component context
                  key: value,
                  value,
                }))}
                fieldName={name}
                currentField={field}
                offsetElementId={CONFIG_EDITOR_RIGHT_PANEL_ID}
                currentLocale='en-US'
                disabled={!editable}
                usageContext='field'
              />
              <Spacer height={2} />
            </div>
          )}
          {type === FieldSchemaType.String && (
            <>
              <FieldWrapper>
                <Field
                  size='md'
                  type='number'
                  id='config.max-char-length'
                  name='config.max-char-length'
                  placeholder={DISABLED_EMPTY_PLACEHOLDER}
                  label={<InputLabel>Max length (char)</InputLabel>}
                  component={InputField}
                  disabled={!editable}
                  validate={validate['max-char-length']}
                  parse={parseInt}
                />
              </FieldWrapper>
              <FieldWrapper>
                <Field
                  id='config.sanitize'
                  name='config.sanitize'
                  component={ReactSelectField}
                  options={STRING_SANITIZATION_OPTIONS}
                  isMulti
                  label={<InputLabel>String sanitization</InputLabel>}
                  disabled={!editable}
                />
              </FieldWrapper>
            </>
          )}
          <FieldWrapper>
            <Field
              size='md'
              id='config.redaction-policy'
              name='config.redaction-policy'
              placeholder={DISABLED_EMPTY_PLACEHOLDER}
              label={<InputLabel>Redaction policy</InputLabel>}
              component={SelectField}
              disabled={!editable}
            >
              {FIELD_POLICY_OPTIONS['redaction-policy'].map(({ label, value }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Field>
          </FieldWrapper>
          <FieldWrapper>
            <Field
              size='md'
              id='config.write-policy'
              name='config.write-policy'
              placeholder={DISABLED_EMPTY_PLACEHOLDER}
              label={<InputLabel>Write policy</InputLabel>}
              component={SelectField}
              disabled={!editable}
            >
              {FIELD_POLICY_OPTIONS['write-policy'].map(({ label, value }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Field>
          </FieldWrapper>
        </BodyContainer>
        <FooterContainer>
          <HStack equal>
            <Button variant='basic' id='field-panel__close-button' onClick={handleClose}>
              Close
            </Button>
            <Tooltip
              side='top'
              trigger={
                <Button
                  variant='danger'
                  id='field-panel__delete-button'
                  data-cy='field-panel__delete-button'
                  disabled={!editable || hasReferences}
                  onClick={deleteField}
                >
                  Delete
                </Button>
              }
              disabled={!editable || !hasReferences}
            >
              <Paragraph>{DELETE_DISABLED_TEXT}</Paragraph>
            </Tooltip>
          </HStack>
        </FooterContainer>
      </Container>
    </FormSection>
  );
};

const DefaultValueInput = ({
  fieldType,
  disabled,
}: {
  fieldType: FieldSchemaType | typeof COUNTRY_CODE_FIELD_TYPE;
  disabled: boolean;
}) => {
  let inputComponent = null;
  const sharedProps = {
    id: 'default-value',
    name: 'default-value',
    placeholder: FIELD_INPUT_PLACEHOLDER,
    label: <InputLabel>Default value</InputLabel>,
    disabled,
  };
  switch (fieldType) {
    case FieldSchemaType.Boolean: {
      inputComponent = (
        <Field
          size='md'
          component={SelectField}
          parse={(v: string) => (v === '' ? null : v === 'true' ? true : false)}
          format={(v: boolean | undefined) => (v == null ? '' : v ? 'true' : 'false')}
          {...sharedProps}
        >
          {['true', 'false'].map((val) => (
            <Option key={val} value={val}>
              {capitalize(val)}
            </Option>
          ))}
        </Field>
      );
      break;
    }
    case FieldSchemaType.String: {
      inputComponent = (
        <Field
          size='md'
          type='text'
          component={InputField}
          parse={(v: string) => (v === '' ? null : v)}
          {...sharedProps}
        />
      );
      break;
    }
    case FieldSchemaType.Number: {
      inputComponent = (
        <Field
          size='md'
          type='number'
          component={InputField}
          parse={(v: string) => (v === '' ? null : parseInt(v))}
          {...sharedProps}
        />
      );
      break;
    }
    case COUNTRY_CODE_FIELD_TYPE: {
      inputComponent = (
        <Field
          size='md'
          component={SelectField}
          {...sharedProps}
          parse={(v: string) => (v === 'none' ? null : v)}
          placeholder='None'
        >
          <Option value='none'>None</Option>
          {Object.entries(COUNTRIES).map(([code, label]) => (
            <Option key={code} value={code}>
              {label} ({code})
            </Option>
          ))}
        </Field>
      );
      break;
    }
    default:
      break;
  }
  return inputComponent != null ? <FieldWrapper>{inputComponent}</FieldWrapper> : null;
};

export default memo(FieldPanel);
